import { CATEGORY_NAMES } from './categoryRouting';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export interface GraphCategoryPointData {
  max_height?: number;
}

export interface GraphDataPoint {
  time?: string;
  data: Record<string, GraphCategoryPointData>;
}

export interface RollingGraphPoint {
  xSeconds: number;
  yValue: number;
}

export interface RollingGraphSeries {
  categoryId: string;
  categoryLabel: string;
  color: string;
  points: RollingGraphPoint[];
}

export interface RollingWindowGraphData {
  playbackHeadTimeMs: number;
  windowSeconds: number;
  series: RollingGraphSeries[];
}

const CATEGORY_COLOR_MAP: Record<string, string> = {
  '-1': '#6b7280', // N/A
  '0': '#8b5a2b', // Ground
  '1': '#38bdf8', // Clear
  '2': '#60a5fa', // Rain?
  '3': '#e5e7eb', // Snow?
  '4': '#94a3b8', // Cloud?
  '5': '#1d4ed8', // Heavy rain
  '10': '#3b82f6', // Warm rain
  '11': '#2563eb', // Cold rain
  '12': '#bfdbfe', // Melting snow
  '14': '#f8fafc', // Snow (no liquid)
  '18': '#67e8f9', // Supercooled liquid
  '21': '#cbd5e1', // Ice cloud (no liquid)
  '25': '#22c55e', // Insects
  '26': '#d97706', // Dust
  '27': '#f59e0b', // Sea salt
  '28': '#fb923c', // Continental pollution
  '29': '#9ca3af', // Smoke
  '30': '#78716c', // Dusty smoke
  '31': '#a16207', // Dusty mix
  '32': '#7c2d12', // Stratospheric ash
  '33': '#a855f7', // Stratospheric sulfate
  '34': '#6d28d9', // Stratospheric smoke
};

function categoryLabel(categoryId: string): string {
  const name = CATEGORY_NAMES[categoryId] ?? 'Unknown';
  return `${categoryId}: ${name}`;
}

function fallbackCategoryColor(categoryId: string): string {
  const numericId = Number(categoryId);
  if (!Number.isFinite(numericId)) {
    return '#94a3b8';
  }

  const hue = ((numericId + 36) * 29) % 360;
  return `hsl(${hue}, 62%, 58%)`;
}

export function colorForCategory(categoryId: string): string {
  return CATEGORY_COLOR_MAP[categoryId] ?? fallbackCategoryColor(categoryId);
}

function toTimestampMs(time?: string): number | null {
  if (!time) {
    return null;
  }

  // Queue data uses "YYYY-MM-DD HH:mm:ss.SSS" without timezone.
  // Normalize to a UTC-safe form before parsing.
  const normalizedTime = time.includes('T') ? time : time.replace(' ', 'T');
  const hasTimezone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(normalizedTime);
  const utcTime = hasTimezone ? normalizedTime : `${normalizedTime}Z`;
  const parsed = dayjs.utc(utcTime);
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.valueOf();
}

export function buildRollingWindowGraphData(
  processedDataQueue: GraphDataPoint[],
  playbackHeadTimeIso: string | null,
  windowSeconds: number
): RollingWindowGraphData {
  const safeWindowSeconds = Number.isFinite(windowSeconds) && windowSeconds > 0 ? windowSeconds : 120;
  const playbackHeadTimeMs =
    (playbackHeadTimeIso ? toTimestampMs(playbackHeadTimeIso) : null) ?? Date.now();

  const halfWindowSeconds = safeWindowSeconds / 2;
  const seriesByCategory: Record<string, RollingGraphSeries> = {};

  for (const point of processedDataQueue) {
    const pointMs = toTimestampMs(point?.time);
    if (pointMs === null) {
      continue;
    }

    const xSeconds = (pointMs - playbackHeadTimeMs) / 1000;
    if (xSeconds < -halfWindowSeconds || xSeconds > halfWindowSeconds) {
      continue;
    }

    const categoryEntries = Object.entries(point.data ?? {});
    for (const [categoryId, categoryData] of categoryEntries) {
      const yValue = categoryData?.max_height;
      if (!Number.isFinite(yValue)) {
        continue;
      }

      if (!seriesByCategory[categoryId]) {
        seriesByCategory[categoryId] = {
          categoryId,
          categoryLabel: categoryLabel(categoryId),
          color: colorForCategory(categoryId),
          points: [],
        };
      }

      seriesByCategory[categoryId].points.push({
        xSeconds,
        yValue: Number(yValue),
      });
    }
  }

  const series = Object.values(seriesByCategory)
    .map((entry) => ({
      ...entry,
      points: entry.points.sort((a, b) => a.xSeconds - b.xSeconds),
    }))
    .sort((a, b) => b.points.length - a.points.length);

  return {
    playbackHeadTimeMs,
    windowSeconds: safeWindowSeconds,
    series,
  };
}
